/**
 * AdminLoginPage Component
 *
 * Status: [IMPLEMENTED]
 * Task: T2.8.2 - Create login page
 *
 * Simple password-only login form at /azzmin-login.
 */

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { setNoTrack } from '../utils/track';

const AdminLoginPage = () => {
  const [password, setPassword] = useState('');
  const { login, isAuthenticated, isLoading, error, clearError } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // F100.1: reaching the login page marks this browser as own traffic so the
  // admin's visits never count in analytics.
  useEffect(() => {
    setNoTrack();
  }, []);

  // If already authenticated, redirect to admin page
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const from = location.state?.from || '/azzmin-access';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, location.state?.from]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.trim()) {
      await login(password);
    }
  };

  // Get any message from redirect (e.g., session expired)
  const redirectMessage = location.state?.message;

  return (
    <div className="min-h-screen bg-neutral-900 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-neutral-800 border border-neutral-700 rounded-xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-neutral-100 mb-2">
              Admin Login
            </h1>
            <p className="text-sm text-neutral-400">
              Resume Generator Access
            </p>
          </div>

          {/* Redirect message */}
          {redirectMessage && (
            <div className="mb-6 p-3 bg-amber-900/30 border border-amber-700 rounded-lg">
              <p className="text-sm text-amber-300">{redirectMessage}</p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mb-6 p-3 bg-red-900/30 border border-red-700 rounded-lg flex justify-between items-center">
              <p className="text-sm text-red-300">{error}</p>
              <button
                onClick={clearError}
                className="text-red-400 hover:text-red-300"
                aria-label="Dismiss error"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-neutral-300 mb-2"
              >
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                placeholder="Enter admin password"
                autoComplete="current-password"
                autoFocus
                className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:opacity-50"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading || !password.trim()}
              className="w-full py-3 bg-sky-600 hover:bg-sky-700 disabled:bg-neutral-600 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Logging in...</span>
                </>
              ) : (
                <span>Login</span>
              )}
            </button>
          </form>

          {/* Back link */}
          <div className="mt-6 text-center">
            <a
              href="/"
              className="text-sm text-neutral-500 hover:text-neutral-400 transition-colors"
            >
              Back to portfolio
            </a>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-neutral-600">
          This is a private admin area
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
